import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { BAND_HATCH, hexToRgb } from '../services/chart-theme';
import type { SciChartSurface, XyDataSeries } from '../charts/scichart';

/**
 * Diagonal hatch over a time-over band. CURRENTLY NOT ATTACHED — see
 * CuringComponent.attachHatch.
 *
 * ============================================================================
 * CORRECTION, 2026-09-17. THE DESKTOP DOES NOT HATCH THESE BANDS.
 *
 * This header used to open by stating, as fact, that "the desktop draws each band
 * as a filled, HATCHED mountain — a LinearGradientBrush behind a diagonal hatch
 * pattern". That was unsourced, it was written by me, and it is wrong.
 *
 * `docs/desktop-chart-parity-spec.md:610` — 1195 lines, produced from a read-only
 * audit of the desktop source — says, citing `GraphViewModel.cs:1509-1520`:
 *
 *     Fill is a `LinearGradientBrush`, alpha 200 -> 100 at 90 degrees, colour
 *     from `ColorPicker.GetColorGradient(i)`
 *
 * The word "hatch" appears ZERO times in that spec. The desktop fills these
 * mountains with a VERTICAL ALPHA GRADIENT and nothing else.
 *
 * So the hatch was an invention, and documenting an invention as parity is worse
 * than the invention: the next person reads the docstring, believes the desktop
 * hatches, and builds further on it. It shipped, Joel saw it on /curing, could not
 * tell what it meant — it has no legend entry, because it labels nothing — and
 * read it as "an artifact leftover from unsuccessful shading attempts". He was
 * right. The attach was removed rather than the file: this is working
 * canvas-over-WebGL machinery, and the parity-correct replacement is a
 * LinearGradientBrush on the series itself, not another overlay.
 * ============================================================================
 *
 * WHY THIS IS AN OVERLAY AND NOT A SERIES PROPERTY. SciChart.js has no pattern
 * fill: `FastMountainRenderableSeries.fill` takes a colour string, there is no
 * texture brush, and IFillPaletteProvider varies the colour per point rather than
 * the paint. A pattern therefore has to be painted above the WebGL surface, on a
 * canvas of our own. (A GRADIENT does not need any of this — SciChart supports
 * `GradientParams` directly, which is why the real fix is on the series.)
 *
 * THE PROBLEM IT WAS REACHING FOR WAS REAL, even though the solution was not
 * parity. Five bands nest inside one another, and tint alone tells you a region is
 * covered without telling you by WHICH band, because every overlap is a new
 * blended colour. What carries that now is each band's full-strength coloured
 * OUTLINE, which also survives the downscale onto a printed page — the hatch never
 * reached the PDF at all, being a fourth canvas where the export composites three.
 *
 * CLIPPED TO THE SILHOUETTE, not to a box. The band's height varies per position
 * — that IS the mountain — so the hatch is clipped to the same polygon the
 * mountain fills: the data series' points along the top, the zero line along the
 * bottom.
 *
 * COORDINATES COME FROM THE AXES, NOT FROM THE DATA. Pan, zoom, the range slider
 * and the liner re-pin all change the mapping, and the axis coordinate
 * calculators are the only thing that knows the current one. Recomputed on the
 * surface's own `rendered` event, so the hatch cannot lag the chart it sits on.
 */

/** One band to hatch: where its outline comes from and what colour it is. */
export interface HatchedBand {
  dataSeries: XyDataSeries;
  color: string;
  /** Follows the legend checkbox — an unchecked series must not leave a hatch. */
  isVisible: () => boolean;
}

@Component({
  selector: 'app-curing-band-hatch',
  template: `<canvas #canvas class="band-hatch"></canvas>`,
  styles: [
    `
      :host {
        position: absolute;
        inset: 0;
        pointer-events: none;
        /* Above the WebGL canvas, below the annotation/legend layer so the
           what-if line, the markers and the legend stay readable through it. */
        z-index: 3;
      }
      .band-hatch {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
      }
    `,
  ],
})
export class CuringBandHatchComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;

  private surface?: SciChartSurface;
  private bands: HatchedBand[] = [];
  private unsubscribeRendered?: () => void;
  private frame = 0;
  private patterns = new Map<string, CanvasPattern>();
  private resizeObserver?: ResizeObserver;

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    // The plot is resized by the window, by the range slider appearing and by the
    // left nav collapsing, none of which redraw the WebGL surface on their own.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.resizeObserver.observe(this.canvasRef.nativeElement.parentElement!);
    }
  }

  /**
   * Bind to a surface. Safe to call again after the chart is rebuilt on a project
   * change — the previous subscription is dropped first.
   */
  attach(surface: SciChartSurface, bands: HatchedBand[]): void {
    this.detach();
    this.surface = surface;
    this.bands = bands;
    this.reparentInto(surface);

    // OUTSIDE ANGULAR. `rendered` fires on every frame of a pan, a zoom and a
    // scrub; running change detection on each would cost more than the hatch.
    this.zone.runOutsideAngular(() => {
      const handler = () => this.schedule();
      (surface as any).rendered?.subscribe(handler);
      this.unsubscribeRendered = () =>
        (surface as any).rendered?.unsubscribe(handler);
    });
    this.schedule();
  }

  /**
   * Move the hatch canvas INSIDE SciChart's chart root, directly above the
   * rendering canvas.
   *
   * Left where Angular puts it — a sibling of the plot div — the overlay is the
   * last positioned element in the container and therefore paints above
   * EVERYTHING SciChart draws, including the legend and the rollover tooltips.
   * The bands routinely reach the top of the plot, so the legend sits inside a
   * hatched region and would be read through diagonal rules.
   *
   * Inserting it into the chart root immediately after the rendering canvas puts
   * it exactly where the desktop's hatch is in the z-order: over the plot, under
   * the annotation, legend and tooltip layers, which SciChart appends after it.
   *
   * If the surface's DOM is not the shape expected, the canvas is left alone —
   * a hatch in the wrong layer is still a hatch, and throwing here would take the
   * chart down for a cosmetic reason.
   */
  private reparentInto(surface: SciChartSurface): void {
    const canvas = this.canvasRef?.nativeElement;
    const root = (surface as any).domChartRoot as HTMLElement | undefined;
    const anchor = ((surface as any).domCanvasWebGL ??
      (surface as any).domCanvas2D) as HTMLElement | undefined;
    if (!canvas || !root || !anchor || anchor.parentElement !== root) {
      return;
    }
    root.insertBefore(canvas, anchor.nextSibling);
  }

  detach(): void {
    if (this.unsubscribeRendered) {
      try {
        this.unsubscribeRendered();
      } catch {
        // The surface may already be deleted; nothing left to unsubscribe from.
      }
      this.unsubscribeRendered = undefined;
    }
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
    this.surface = undefined;
    this.bands = [];
    this.clear();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.detach();
  }

  /** Coalesce to one paint per frame — `rendered` can fire several times. */
  private schedule(): void {
    if (this.frame) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      try {
        this.paint();
      } catch {
        // A hatch that cannot be drawn must never take the chart down with it.
        // The translucent mountain fill underneath is still a correct, readable
        // band; this layer is enrichment, so it fails silently by design.
        this.clear();
      }
    });
  }

  private context(): CanvasRenderingContext2D | null {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) {
      return null;
    }
    const host = canvas.parentElement;
    const width = host?.clientWidth ?? 0;
    const height = host?.clientHeight ?? 0;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(width * dpr) ||
        canvas.height !== Math.round(height * dpr)) {
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    return ctx;
  }

  private clear(): void {
    const canvas = this.canvasRef?.nativeElement;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  private paint(): void {
    const surface = this.surface;
    const ctx = this.context();
    if (!surface || !ctx) {
      return;
    }

    const rect = (surface as any).seriesViewRect;
    if (!rect || rect.width <= 0 || rect.height <= 0) {
      return;
    }

    for (const band of this.bands) {
      if (!band.isVisible()) {
        continue;
      }
      const path = this.silhouette(surface, band, rect);
      if (!path) {
        continue;
      }
      const pattern = this.patternFor(ctx, band.color);
      if (!pattern) {
        continue;
      }
      ctx.save();
      // Clip to the plot area as well as the band. A band whose values run past
      // the visible y range would otherwise paint hatch over the axis furniture.
      ctx.beginPath();
      ctx.rect(rect.x, rect.y, rect.width, rect.height);
      ctx.clip();
      ctx.fillStyle = pattern;
      ctx.fill(path);
      ctx.restore();
    }
  }

  /**
   * The band's outline in screen pixels: along the top of the mountain, then back
   * along the zero line.
   */
  private silhouette(
    surface: SciChartSurface,
    band: HatchedBand,
    rect: { x: number; y: number; width: number; height: number }
  ): Path2D | null {
    const series = band.dataSeries as any;
    const count: number = series?.count?.() ?? 0;
    if (count < 2) {
      return null;
    }

    // The renderable series decides which axes it is on; read them from the
    // surface by id rather than assuming, so a future axis rename cannot put the
    // hatch on the temperature scale.
    const renderable = surface.renderableSeries
      .asArray()
      .find((rs) => (rs as any).dataSeries === band.dataSeries);
    if (!renderable) {
      return null;
    }
    const xAxis = surface.xAxes.getById((renderable as any).xAxisId);
    const yAxis = surface.yAxes.getById((renderable as any).yAxisId);
    if (!xAxis || !yAxis) {
      return null;
    }
    const xCalc = (xAxis as any).getCurrentCoordinateCalculator?.();
    const yCalc = (yAxis as any).getCurrentCoordinateCalculator?.();
    if (!xCalc || !yCalc) {
      return null;
    }

    const xs = series.getNativeXValues();
    const ys = series.getNativeYValues();
    // Coordinate calculators work in the series view's own space; the canvas
    // covers the whole chart element, so every coordinate is offset by the plot
    // rect's origin.
    const baseline = rect.y + yCalc.getCoordinate(0);

    const path = new Path2D();
    let started = false;
    let lastX = 0;
    for (let i = 0; i < count; i++) {
      const px = rect.x + xCalc.getCoordinate(xs.get(i));
      const py = rect.y + yCalc.getCoordinate(ys.get(i));
      if (!Number.isFinite(px) || !Number.isFinite(py)) {
        continue;
      }
      if (!started) {
        path.moveTo(px, baseline);
        started = true;
      }
      path.lineTo(px, py);
      lastX = px;
    }
    if (!started) {
      return null;
    }
    path.lineTo(lastX, baseline);
    path.closePath();
    return path;
  }

  /** One tile per colour, built once and reused across frames. */
  private patternFor(
    ctx: CanvasRenderingContext2D,
    color: string
  ): CanvasPattern | null {
    const cached = this.patterns.get(color);
    if (cached) {
      return cached;
    }
    const size = BAND_HATCH.tilePx;
    const tile = document.createElement('canvas');
    tile.width = size;
    tile.height = size;
    const tctx = tile.getContext('2d');
    if (!tctx) {
      return null;
    }
    const { r, g, b } = hexToRgb(color);
    tctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${BAND_HATCH.opacity})`;
    tctx.lineWidth = BAND_HATCH.penThicknessPx;
    // 45-degree RISING rules. Drawn as two segments so the diagonal is continuous
    // across tile edges — a single corner-to-corner line leaves visible breaks.
    tctx.beginPath();
    tctx.moveTo(-size, size);
    tctx.lineTo(size, -size);
    tctx.moveTo(0, size * 2);
    tctx.lineTo(size * 2, 0);
    tctx.stroke();

    const pattern = ctx.createPattern(tile, 'repeat');
    if (pattern) {
      this.patterns.set(color, pattern);
    }
    return pattern;
  }
}
